import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { select } from 'd3-selection'
import { zoom as d3Zoom, zoomIdentity, type ZoomBehavior, type ZoomTransform } from 'd3-zoom'
import { drag as d3Drag } from 'd3-drag'
import { easeBackOut, easeCubicInOut } from 'd3-ease'
import { linkVertical } from 'd3-shape'
import 'd3-transition'
import type { ForceLink, Simulation } from 'd3-force'
import type { MockEdge, MockNode, MockNodeType } from './types'
import { NODE_TYPE_STYLES, SELECTED_NODE_BG, SELECTED_NODE_FG } from './nodeIcons'
import type { GraphLayout } from './layoutOptions'
import type { CanvasHandle, HoverInfo, Overview, Viewport } from './KnowledgeGraphCanvas'
import {
  buildModel,
  collapseToDepth,
  expandAll,
  labelFontSize,
  seedRadialPositions,
  toggleChildren,
  treePositions,
  visibleGraph,
  type GraphLinkDatum,
  type GraphModel,
  type GraphNodeDatum,
} from './d3/graphModel'
import { createSimulation, FORCE } from './d3/forces'

/**
 * The Knowledge Graph canvas in D3 (DSGN-63 radial spec).
 *
 * Radial View is a force layout whose ENTRANCE is physics: nodes are seeded
 * on rings longer than the final link distances, painted once at full scale,
 * and the forces contract the stretched chains into the settled graph. Tree
 * View is a top-down tree with curved links (leaf groups as grids beside
 * their parent, cross links dashed). Clicking a node scales it up,
 * dims everything but its parent and children, ripples a ring, pulses a dot
 * along each edge and re-centres the camera. Collapsing moves children away;
 * expanding grows them back out of the parent.
 *
 * It keeps the `CanvasHandle` contract of the cytoscape canvas so the minimap
 * and the zoom controls are unchanged.
 */
export interface D3CanvasHandle extends CanvasHandle {
  /** Stop, re-seed the stretched positions, reset selection/zoom/styles and run the entrance again. */
  replayEntrance: () => void
  collapseToSites: () => void
  expandAll: () => void
  /** Expand or collapse one node's children. Returns false for a leaf. */
  toggleChildren: (id: string) => boolean
  isCollapsed: (id: string) => boolean
  hasChildren: (id: string) => boolean
}

interface Props {
  nodes: MockNode[]
  edges: MockEdge[]
  layout?: GraphLayout
  enabledTypes: Set<MockNodeType>
  filteredOutIds?: Set<string>
  showEntityLabels?: boolean
  showEdgeLabels?: boolean
  showOrphans?: boolean
  /**
   * Draw a node with no link as an ordinary node: no parking beside the graph,
   * no hiding. The page sets this while the toolbar search is on, because a
   * search keeps only the matching nodes and most of them lose their parent —
   * they are the result, not orphans of it.
   */
  keepUnlinked?: boolean
  lineWidth?: number
  selectedNodeId?: string | null
  /**
   * A connection picked in the details panel. Its edge draws teal and both
   * ends take the selected look; matched by endpoints in either direction,
   * because a tree edge's drawn id (`parent>child`) is not the edge's own id.
   */
  selectedLink?: { source: string; target: string } | null
  onNodeSelect?: (nodeId: string | null) => void
  onNodeHover?: (info: HoverInfo | null) => void
}

const ZOOM_EXTENT: [number, number] = [0.4, 2.5]
/** Tree View zooms out further: a whole tenant's tree is many screens tall (the design frames it at 9%). */
const TREE_ZOOM_EXTENT: [number, number] = [0.05, 2.5]
/**
 * Tree View draws its cross links at rest only while there are this few, as
 * the design does; past that (a real tenant has hundreds of sheet pairs) they
 * are drawn only for the selected node and its parent, or the tree is lost
 * under the mesh.
 */
export const TREE_CROSS_REST_MAX = 40
const LINK_GRAY = { light: '#d4d4d8', dark: '#525252' }
/** Tree View draws the non-hierarchy connections (cross-sheet links) as the design does: straight, dashed, steel blue. */
const CROSS_LINK = { light: '#6b97ab', dark: '#7aa7bb' }
const CROSS_LINK_DASH = '6 4'
/** Tree View's parent → child curves: the design's darker grey, so the tree reads at 10% zoom. */
const TREE_LINK_GRAY = { light: '#a3a3a3', dark: '#737373' }
const ORPHAN_AREA_GAP = 160
const ORPHAN_AREA_ROW = 72
/**
 * The selected node's colours, sampled off the design: the ring and icon take
 * the teal, the disc a pale wash of it, and the label the same teal. Selection
 * recolours — it does NOT thicken the ring, which read as a heavy outline.
 */
const SELECTED_FG = SELECTED_NODE_FG
const SELECTED_BG = SELECTED_NODE_BG


const EASE_BACK = easeBackOut.overshoot(1.8)

// d3 interpolates `transform` strings through the SVG DOM, which jsdom lacks
// and browsers parse per frame; these tween the two shapes we use by number.
const readScale = (el: SVGGElement) => Number(/scale\(([^)]+)\)/.exec(el.getAttribute('transform') ?? '')?.[1] ?? 1)
const scaleTween = (target: (d: GraphNodeDatum) => number) =>
  function (this: SVGGElement, d: GraphNodeDatum) {
    const from = readScale(this)
    const to = target(d)
    return (t: number) => `scale(${from + (to - from) * t})`
  }

const isDark = () =>
  typeof document !== 'undefined' &&
  (document.documentElement.classList.contains('dark') ||
    (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches === true))

const linkGray = () => (isDark() ? LINK_GRAY.dark : LINK_GRAY.light)
const crossLinkBlue = () => (isDark() ? CROSS_LINK.dark : CROSS_LINK.light)
/** A cross link in Tree View; every other link keeps the plain grey of both views. */
const isTreeCross = (l: GraphLinkDatum, layout: GraphLayout) => layout === 'tree' && !l.hierarchy
const restingStroke = (l: GraphLinkDatum, layout: GraphLayout) =>
  isTreeCross(l, layout) ? crossLinkBlue() : layout === 'tree' ? (isDark() ? TREE_LINK_GRAY.dark : TREE_LINK_GRAY.light) : linkGray()

/** A node and every ancestor above it. */
const ancestorIds = (model: GraphModel, id: string): Set<string> => {
  const out = new Set<string>()
  for (let cur = model.byId.get(id); cur && !out.has(cur.id); cur = cur.parentId ? model.byId.get(cur.parentId) : undefined) out.add(cur.id)
  return out
}

const mid = (l: GraphLinkDatum) => ({ x: (l.source.x + l.target.x) / 2, y: (l.source.y + l.target.y) / 2 })

const curvedLink = linkVertical<GraphLinkDatum, GraphNodeDatum>()
  .x((d) => d.x)
  .y((d) => d.y)

const straightLink = (l: GraphLinkDatum) => `M${l.source.x},${l.source.y}L${l.target.x},${l.target.y}`
/** Tree View curves the parent → child links only; cross links run straight between their ends. */
const linkPath = (l: GraphLinkDatum, layout: GraphLayout) => (layout === 'tree' && l.hierarchy ? curvedLink(l) : straightLink(l))

const KnowledgeGraphD3Canvas = forwardRef<D3CanvasHandle, Props>(function KnowledgeGraphD3Canvas(
  {
    nodes,
    edges,
    layout = 'radial',
    enabledTypes,
    filteredOutIds,
    showEntityLabels = true,
    showEdgeLabels = false,
    showOrphans = false,
    keepUnlinked = false,
    lineWidth = 1.5,
    selectedNodeId = null,
    selectedLink = null,
    onNodeSelect,
    onNodeHover,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const modelRef = useRef<GraphModel | null>(null)
  const simRef = useRef<Simulation<GraphNodeDatum, GraphLinkDatum> | null>(null)
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null)
  const transformRef = useRef<ZoomTransform>(zoomIdentity)
  const sizeRef = useRef({ w: 800, h: 600 })
  const zoomSubs = useRef<Set<(z: number) => void>>(new Set())
  const overviewSubs = useRef<Set<(ov: Overview) => void>>(new Set())
  const overviewAtRef = useRef(0)
  const viewportSubs = useRef<Set<(vp: Viewport) => void>>(new Set())
  const selectedRef = useRef<string | null>(null)
  // The link drawn teal for the connection picked in the details panel.
  const pickedLinkIdRef = useRef<string | null>(null)
  // The layout the canvas is drawn in; the layout-switch effect advances it.
  const appliedLayoutRef = useRef<GraphLayout>(layout)
  /**
   * What the reader set up on the canvas (collapsed nodes, positions, camera),
   * kept when the canvas is torn down. Tree View draws its own graph (Area →
   * System groups), so a view switch rebuilds the canvas; the rebuild restores
   * these so the switch keeps them as it does for a graph both views share.
   */
  const carryRef = useRef<{ collapsed: Set<string>; pos: Map<string, { x: number; y: number }>; transform: ZoomTransform } | null>(null)
  const selectedNodeIdRef = useRef(selectedNodeId)
  selectedNodeIdRef.current = selectedNodeId
  const selectedLinkRef = useRef<{ source: string; target: string } | null>(selectedLink)
  selectedLinkRef.current = selectedLink
  // Latest props, read by handlers that live across renders.
  const propsRef = useRef({ layout, enabledTypes, filteredOutIds, showEntityLabels, showEdgeLabels, showOrphans, keepUnlinked, lineWidth, onNodeSelect, onNodeHover })
  propsRef.current = { layout, enabledTypes, filteredOutIds, showEntityLabels, showEdgeLabels, showOrphans, keepUnlinked, lineWidth, onNodeSelect, onNodeHover }

  const layers = () => {
    const svg = select(svgRef.current as SVGSVGElement)
    const zoomG = svg.select<SVGGElement>('g.zoom')
    return {
      svg,
      zoomG,
      links: zoomG.select<SVGGElement>('g.links'),
      effects: zoomG.select<SVGGElement>('g.effects'),
      nodes: zoomG.select<SVGGElement>('g.nodes'),
    }
  }

  const viewport = (): Viewport => {
    const t = transformRef.current
    const { w, h } = sizeRef.current
    return { x1: (-w / 2 - t.x) / t.k, y1: (-h / 2 - t.y) / t.k, x2: (w / 2 - t.x) / t.k, y2: (h / 2 - t.y) / t.k }
  }
  const notifyViewport = () => {
    const vp = viewport()
    viewportSubs.current.forEach((fn) => fn(vp))
    zoomSubs.current.forEach((fn) => fn(transformRef.current.k))
  }

  const buildOverview = (): Overview | null => {
    const model = modelRef.current
    if (!model) return null
    const { nodes: vis, links } = visibleGraph(model)
    return {
      nodes: vis.map((d) => ({ id: d.id, x: d.x, y: d.y, type: d.type })),
      // The thumbnail leaves out the cross links Tree View is not drawing at rest.
      edges: (crowdedTreeCross(links) ? links.filter((l) => l.hierarchy) : links).map((l) => ({ source: l.source.id, target: l.target.id })),
      bbox: bbox(vis),
    }
  }
  /** Tell the minimap where the nodes are: at once when a layout is shown or
   *  the graph changes, and ~12 times a second while the physics moves them,
   *  so the thumbnail follows the main view. */
  const notifyOverview = (force = false) => {
    if (overviewSubs.current.size === 0) return
    const now = Date.now()
    if (!force && now - overviewAtRef.current < 80) return
    overviewAtRef.current = now
    const ov = buildOverview()
    if (ov) overviewSubs.current.forEach((fn) => fn(ov))
  }

  /** Positions → DOM. The simulation's tick handler, also called once by hand. */
  const tick = () => {
    const { links, nodes: nodesG } = layers()
    const layout = propsRef.current.layout
    links.selectAll<SVGPathElement, GraphLinkDatum>('path.link').attr('d', (l) => linkPath(l, layout))
    links
      .selectAll<SVGTextElement, GraphLinkDatum>('text.link-label')
      .attr('x', (l) => mid(l).x)
      .attr('y', (l) => mid(l).y - 4)
    nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node').attr('transform', (d) => `translate(${d.x},${d.y})`)
    notifyOverview()
  }

  const bbox = (ds: GraphNodeDatum[]) => {
    if (ds.length === 0) return { x1: -1, y1: -1, x2: 1, y2: 1 }
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity
    for (const d of ds) {
      x1 = Math.min(x1, d.x - d.r); y1 = Math.min(y1, d.y - d.r); x2 = Math.max(x2, d.x + d.r); y2 = Math.max(y2, d.y + d.r + 18)
    }
    return { x1, y1, x2, y2 }
  }

  /** Orphans of the filtered graph: parked in a column beside it, or hidden.
   *  The toolbar search is not applied here: the page hands the canvas the
   *  graph already narrowed to the matches and their ancestors, as the design
   *  draws it, so a search re-lays out what is left rather than dimming. */
  const applyVisibility = () => {
    const model = modelRef.current
    if (!model) return
    const { enabledTypes: types, filteredOutIds: out, showOrphans: orphansOn, keepUnlinked, showEntityLabels: labelsOn, showEdgeLabels: edgeLabelsOn, lineWidth: width } = propsRef.current
    const { nodes: vis, links } = visibleGraph(model)
    const dimmed = new Set<string>()
    for (const d of vis) {
      const typeOk = types.size === 0 || types.has(d.type) || d.type === 'enterprise'
      const facetOk = !out?.has(d.id)
      if (!(typeOk && facetOk)) dimmed.add(d.id)
    }
    const linkDimmed = (l: GraphLinkDatum) => dimmed.has(l.source.id) || dimmed.has(l.target.id)
    const connected = new Set<string>()
    for (const l of links) if (!linkDimmed(l)) { connected.add(l.source.id); connected.add(l.target.id) }
    const orphans = keepUnlinked ? [] : vis.filter((d) => d.type !== 'enterprise' && !connected.has(d.id))
    const orphanIds = new Set(orphans.map((d) => d.id))
    if (orphansOn && orphans.length > 0) {
      const rest = vis.filter((d) => !orphanIds.has(d.id))
      const bb = bbox(rest)
      let y = bb.y1
      for (const d of orphans) { d.fx = bb.x2 + ORPHAN_AREA_GAP; d.fy = y; d.x = d.fx; d.y = d.fy; y += ORPHAN_AREA_ROW }
    } else {
      for (const d of orphans) { if (d.fx !== undefined && d.fx !== null) { d.fx = null; d.fy = null } }
    }
    const { links: linksG, nodes: nodesG } = layers()
    nodesG
      .selectAll<SVGGElement, GraphNodeDatum>('g.node')
      .classed('dimmed', (d) => dimmed.has(d.id))
      .classed('orphan', (d) => orphanIds.has(d.id))
      .style('display', (d) => (orphanIds.has(d.id) && !orphansOn ? 'none' : 'inline'))
      .select('text.label')
      .style('display', labelsOn ? 'inline' : 'none')
    linksG
      .selectAll<SVGPathElement, GraphLinkDatum>('path.link')
      .classed('dimmed', (l) => linkDimmed(l) || orphanIds.has(l.source.id) || orphanIds.has(l.target.id))
      .style('display', (l) => ((orphanIds.has(l.source.id) || orphanIds.has(l.target.id)) && !orphansOn ? 'none' : 'inline'))
    linksG.selectAll<SVGTextElement, GraphLinkDatum>('text.link-label').style('display', edgeLabelsOn ? 'inline' : 'none')
    linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link').each(function () {
      const el = select(this)
      if (!el.classed('focused') && !el.classed('context')) el.attr('stroke-width', width)
    })
    styleLinks()
    tick()
  }

  /** Resting link colour and dash for the current layout; a picked link keeps its teal. */
  const styleLinks = () => {
    const layout = propsRef.current.layout
    const { links: linksG } = layers()
    linksG
      .selectAll<SVGPathElement, GraphLinkDatum>('path.link')
      .attr('stroke-dasharray', (l) => (isTreeCross(l, layout) ? CROSS_LINK_DASH : null))
      .filter(function () { return !select(this).classed('picked') })
      .attr('stroke', (l) => restingStroke(l, layout))
    hideCrowdedCrossLinks()
  }

  /** Past TREE_CROSS_REST_MAX, Tree View keeps only the cross links at the selected node or its parent. */
  const hideCrowdedCrossLinks = () => {
    const model = modelRef.current
    if (!model) return
    const layout = propsRef.current.layout
    const { links: linksG } = layers()
    const paths = linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link')
    const crowded = crowdedTreeCross(paths.data())
    const sel = selectedRef.current
    const near = new Set<string>()
    if (sel) {
      near.add(sel)
      const parent = model.byId.get(sel)?.parentId
      if (parent) near.add(parent)
    }
    const hidden = (l: GraphLinkDatum) =>
      crowded && isTreeCross(l, layout) && l.id !== pickedLinkIdRef.current && !near.has(l.source.id) && !near.has(l.target.id)
    // An attribute, not `display`: applyVisibility owns the inline display (orphans).
    paths.attr('visibility', (l) => (hidden(l) ? 'hidden' : null))
    linksG.selectAll<SVGTextElement, GraphLinkDatum>('text.link-label').attr('visibility', (l) => (hidden(l) ? 'hidden' : null))
  }

  /** Tree View has more cross links than it draws at rest. */
  const crowdedTreeCross = (links: GraphLinkDatum[]): boolean => {
    const layout = propsRef.current.layout
    let n = 0
    for (const l of links) if (isTreeCross(l, layout)) n += 1
    return n > TREE_CROSS_REST_MAX
  }

  const zoomExtent = (): [number, number] => (propsRef.current.layout === 'tree' ? TREE_ZOOM_EXTENT : ZOOM_EXTENT)

  /** Click on a node. The design keeps the graph exactly as it was — no
   *  dimming, no recoloured edges, no camera move, no ripple or edge pulses.
   *  The selected node alone is recoloured teal: ring, icon and label. The
   *  ring KEEPS its 1.5 width — thickening it read as a bold outline.
   *  A connection picked in the panel adds its edge, drawn teal, and its other
   *  end, which takes the same look as the selected node. */
  const applyFocus = (id: string | null) => {
    const model = modelRef.current
    if (!model) return
    selectedRef.current = id
    const { nodes: nodesG, links: linksG } = layers()
    const sel = id ? model.byId.get(id) ?? null : null
    const pick = sel ? selectedLinkRef.current : null
    const exact = (l: GraphLinkDatum): boolean =>
      !!pick &&
      ((l.source.id === pick.source && l.target.id === pick.target) || (l.source.id === pick.target && l.target.id === pick.source))
    const linkSel = linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link')
    // Tree View draws a tag-to-tag connection as one link between the two
    // ends' sheets (treeViewGraph); a picked connection lights that link.
    let picked = linkSel.data().find(exact)
    if (pick && !picked && propsRef.current.layout === 'tree') {
      const a = ancestorIds(model, pick.source)
      const b = ancestorIds(model, pick.target)
      picked = linkSel.data().find((l) =>
        !l.hierarchy && ((a.has(l.source.id) && b.has(l.target.id)) || (b.has(l.source.id) && a.has(l.target.id))))
    }
    pickedLinkIdRef.current = picked?.id ?? null
    const isPicked = (l: GraphLinkDatum): boolean => !!picked && l.id === picked.id
    linkSel
      .classed('picked', isPicked)
      .attr('stroke', (l) => (isPicked(l) ? SELECTED_FG : restingStroke(l, propsRef.current.layout)))
      .attr('stroke-opacity', 1)
    // Drawn last so no grey edge crosses over it.
    linkSel.filter(isPicked).raise()
    const lit = new Set<string>()
    if (sel) lit.add(sel.id)
    if (pick) { lit.add(pick.source); lit.add(pick.target) }
    const nodeSel = nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node')
    nodeSel.classed('selected', (d) => lit.has(d.id))
    const isSel = (d: GraphNodeDatum): boolean => lit.has(d.id)
    nodeSel
      .select('circle')
      .attr('fill', (d: GraphNodeDatum) => (isSel(d) ? SELECTED_BG : NODE_TYPE_STYLES[d.type].bg))
      .attr('stroke', (d: GraphNodeDatum) => (isSel(d) ? SELECTED_FG : NODE_TYPE_STYLES[d.type].fg))
      .attr('stroke-width', 1.5)
    nodeSel
      .select('svg.icon')
      .attr('stroke', (d: GraphNodeDatum) => (isSel(d) ? SELECTED_FG : NODE_TYPE_STYLES[d.type].fg))
    hideCrowdedCrossLinks()
  }

  /** Data join for the visible graph. In Radial View entering and exiting
   *  nodes and links animate per the spec; Tree View is static and places
   *  everything at once. */
  const render = (opts: { entering: boolean }) => {
    const animate = opts.entering && propsRef.current.layout === 'radial'
    const model = modelRef.current
    if (!model) return
    const { links: linksG, nodes: nodesG } = layers()
    const { nodes: vis, links } = visibleGraph(model)
    const layout = propsRef.current.layout
    const width = propsRef.current.lineWidth

    const link = linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link').data(links, (l) => l.id)
    if (animate) link.exit().transition().duration(250).style('opacity', 0).remove()
    else link.exit().remove()
    const linkEnter = link
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('fill', 'none')
      .attr('stroke', (l) => restingStroke(l, layout))
      .attr('stroke-dasharray', (l) => (isTreeCross(l, layout) ? CROSS_LINK_DASH : null))
      .attr('stroke-width', width)
      .attr('d', (l) => linkPath(l, layout))
    if (animate) linkEnter.style('opacity', 0).transition().duration(600).style('opacity', 1)
    const label = linksG.selectAll<SVGTextElement, GraphLinkDatum>('text.link-label').data(links.filter((l) => l.label || l.hierarchy), (l) => l.id)
    label.exit().remove()
    label
      .enter()
      .append('text')
      .attr('class', 'link-label fill-neutral-500 dark:fill-neutral-400')
      .attr('font-size', 10)
      .attr('text-anchor', 'middle')
      .text((l) => l.label ?? (l.source.type === 'enterprise' ? 'has site' : l.target.type === 'documents' ? 'documented by' : 'contains'))

    const node = nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node').data(vis, (d) => d.id)
    const exit = node.exit<GraphNodeDatum>()
    if (animate) {
      exit.select<SVGGElement>('g.inner').transition().duration(250).attrTween('transform', scaleTween(() => 0))
      exit.transition().duration(250).remove()
    } else {
      exit.remove()
    }
    const enter = node.enter().append('g').attr('class', 'node').attr('transform', (d) => `translate(${d.x},${d.y})`).style('cursor', 'pointer')
    const inner = enter.append('g').attr('class', 'inner')
    inner
      .append('circle')
      .attr('r', (d) => d.r)
      .attr('fill', (d) => NODE_TYPE_STYLES[d.type].bg)
      .attr('stroke', (d) => NODE_TYPE_STYLES[d.type].fg)
      .attr('stroke-width', 1.5)
    inner
      .append('svg')
      .attr('class', 'icon')
      .attr('viewBox', '0 0 24 24')
      .attr('fill', 'none')
      .attr('stroke-width', 2)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('stroke', (d) => NODE_TYPE_STYLES[d.type].fg)
      .attr('width', (d) => d.r * 1.05)
      .attr('height', (d) => d.r * 1.05)
      .attr('x', (d) => -d.r * 0.525)
      .attr('y', (d) => -d.r * 0.525)
      .html((d) => NODE_TYPE_STYLES[d.type].iconPaths)
    inner
      .append('text')
      .attr('class', 'label fill-neutral-500 dark:fill-neutral-400')
      .attr('text-anchor', 'middle')
      .attr('y', (d) => d.r + 14)
      .attr('font-size', (d) => labelFontSize(d.depth))
      .text((d) => d.displayLabel)
    // The full name for a truncated label, as the native tooltip.
    enter.append('title').text((d) => d.label)
    if (animate) {
      inner.attr('transform', 'scale(0)').transition().duration(650).ease(EASE_BACK).attrTween('transform', scaleTween((d) => d.s))
    } else {
      inner.attr('transform', (d) => `scale(${d.s})`)
    }
    enter
      .on('click', (event: MouseEvent, d) => {
        event.stopPropagation()
        // A node click drops the picked connection; the page clears its own copy.
        selectedLinkRef.current = null
        propsRef.current.onNodeSelect?.(d.id)
        applyFocus(d.id)
      })
      .on('mouseenter', function (_event, d) {
        if (!propsRef.current.onNodeHover || !svgRef.current) return
        const t = transformRef.current
        const { w, h } = sizeRef.current
        const rect = svgRef.current.getBoundingClientRect()
        const sx = rect.width / (w || 1), sy = rect.height / (h || 1)
        propsRef.current.onNodeHover({ id: d.id, x: (t.applyX(d.x) + w / 2 + d.r * t.k) * sx, y: (t.applyY(d.y) + h / 2) * sy })
      })
      .on('mouseleave', () => propsRef.current.onNodeHover?.(null))
      .call(
        d3Drag<SVGGElement, GraphNodeDatum>()
          .on('start', (event, d) => {
            if (propsRef.current.layout !== 'radial') return
            if (!event.active) simRef.current?.alphaTarget(0.3).restart()
            d.fx = d.x; d.fy = d.y
          })
          .on('drag', (event, d) => {
            if (propsRef.current.layout !== 'radial') return
            d.fx = event.x; d.fy = event.y
          })
          .on('end', (event, d) => {
            if (propsRef.current.layout !== 'radial') return
            if (!event.active) simRef.current?.alphaTarget(0)
            d.fx = null; d.fy = null
          }),
      )
    // Collapsed nodes get a dashed ring.
    nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node').select('circle').attr('stroke-dasharray', (d) => (d.collapsed ? '3 2' : null))

    // Feed the simulation the visible set.
    const sim = simRef.current
    if (sim) {
      sim.nodes(vis)
      sim.force<ForceLink<GraphNodeDatum, GraphLinkDatum>>('link')?.links(links)
    }
    applyVisibility()
    notifyOverview(true)
  }

  /** Tree View: stop the physics and place the tree at once (no animation),
   *  laid out around where the reader is looking so the view never has to move. */
  const layoutTree = () => {
    const model = modelRef.current
    if (!model) return
    simRef.current?.stop()
    const pos = treePositions(model)
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity
    pos.forEach((p) => { x1 = Math.min(x1, p.x); y1 = Math.min(y1, p.y); x2 = Math.max(x2, p.x); y2 = Math.max(y2, p.y) })
    const vp = viewport()
    const dx = pos.size ? (vp.x1 + vp.x2) / 2 - (x1 + x2) / 2 : 0
    const dy = pos.size ? (vp.y1 + vp.y2) / 2 - (y1 + y2) / 2 : 0
    for (const d of model.nodes) {
      const p = pos.get(d.id)
      if (p) { d.x = p.x + dx; d.y = p.y + dy; d.vx = 0; d.vy = 0 }
    }
    const { links: linksG, nodes: nodesG } = layers()
    nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node').interrupt('layout')
    linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link').interrupt('layout')
    tick()
    notifyOverview(true)
  }

  const fitTo = (animate: boolean) => {
    const model = modelRef.current
    const zoomB = zoomRef.current
    if (!model || !zoomB || !svgRef.current) return
    const { nodes: vis } = visibleGraph(model)
    const bb = bbox(vis)
    const { w, h } = sizeRef.current
    const pad = 60
    const [kMin, kMax] = zoomExtent()
    const k = Math.max(kMin, Math.min(kMax, Math.min((w - pad * 2) / Math.max(bb.x2 - bb.x1, 1), (h - pad * 2) / Math.max(bb.y2 - bb.y1, 1))))
    const cx = (bb.x1 + bb.x2) / 2, cy = (bb.y1 + bb.y2) / 2
    const t = zoomIdentity.scale(k).translate(-cx, -cy)
    const svg = select(svgRef.current)
    if (animate) svg.transition().duration(400).ease(easeCubicInOut).call(zoomB.transform, t)
    else svg.call(zoomB.transform, t)
  }

  /** The entrance: stretched seed, one hand tick at full scale, then physics from alpha 0.8. */
  const runEntrance = () => {
    const model = modelRef.current
    const sim = simRef.current
    if (!model || !sim) return
    sim.stop()
    seedRadialPositions(model)
    for (const d of model.nodes) { d.s = 1; d.fx = null; d.fy = null }
    const { nodes: nodesG } = layers()
    nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node').interrupt().style('opacity', 1).select('g.inner').interrupt().attr('transform', 'scale(1)')
    tick()
    sim.alpha(FORCE.entranceAlpha).restart()
  }

  // Build the SVG, zoom and simulation once per nodes/edges set.
  useEffect(() => {
    const svgEl = svgRef.current
    const container = containerRef.current
    if (!svgEl || !container) return
    const measure = () => {
      const rect = container.getBoundingClientRect()
      const w = rect.width || 800, h = rect.height || 600
      sizeRef.current = { w, h }
      svgEl.setAttribute('viewBox', `${-w / 2} ${-h / 2} ${w} ${h}`)
      notifyViewport()
    }
    measure()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(container)

    const svg = select(svgEl)
    svg.selectAll('*').remove()
    const zoomG = svg.append('g').attr('class', 'zoom')
    zoomG.append('g').attr('class', 'links')
    zoomG.append('g').attr('class', 'effects')
    zoomG.append('g').attr('class', 'nodes')

    const zoomB = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent(zoomExtent())
      // The viewBox is centred on 0,0; saying so here keeps d3-zoom off the
      // SVG viewBox API (absent in jsdom) and makes translateTo/scaleTo exact.
      .extent((): [[number, number], [number, number]] => {
        const { w, h } = sizeRef.current
        return [[-w / 2, -h / 2], [w / 2, h / 2]]
      })
      .filter((event: Event) => event.type !== 'dblclick')
      .on('zoom', (event) => {
        transformRef.current = event.transform
        zoomG.attr('transform', event.transform.toString())
        notifyViewport()
      })
    svg.call(zoomB).on('dblclick.zoom', null)
    zoomRef.current = zoomB
    svg.on('click', () => {
      propsRef.current.onNodeSelect?.(null)
      applyFocus(null)
    })

    const model = buildModel(nodes, edges)
    modelRef.current = model
    // A rebuild that comes with a view switch carries the reader's state over.
    const carry = appliedLayoutRef.current !== propsRef.current.layout ? carryRef.current : null
    carryRef.current = null
    if (carry) for (const d of model.nodes) d.collapsed = carry.collapsed.has(d.id) && d.childIds.length > 0
    const { nodes: vis, links } = visibleGraph(model)
    const sim = createSimulation(vis, links)
    sim.on('tick', tick)
    // No re-fit when the physics settles: the reader asked for the entrance
    // to end where it ends, and to re-frame with the minimap's fit button.
    sim.on('end', () => notifyOverview(true))
    simRef.current = sim
    selectedRef.current = null

    seedRadialPositions(model)
    if (carry) {
      for (const d of model.nodes) {
        const p = carry.pos.get(d.id)
        if (p) { d.x = p.x; d.y = p.y }
      }
    }
    render({ entering: false })
    // Open at 100% (natural size, k = 1) centred on the graph's centre, as the
    // design's reference frames are drawn; the minimap's fit button frames all.
    svg.call(zoomB.transform, carry ? carry.transform : zoomIdentity)
    if (propsRef.current.layout === 'tree') {
      layoutTree()
    } else {
      // The entrance: paint the stretched seed once, then let the physics
      // contract it. The camera does not move again on its own.
      tick()
      sim.alpha(FORCE.entranceAlpha).restart()
    }
    const keepSelected = selectedNodeIdRef.current
    if (carry && keepSelected && model.byId.has(keepSelected)) applyFocus(keepSelected)
    notifyOverview(true)
    return () => {
      carryRef.current = {
        collapsed: new Set(model.nodes.filter((d) => d.collapsed).map((d) => d.id)),
        pos: new Map(model.nodes.map((d) => [d.id, { x: d.x, y: d.y }])),
        transform: transformRef.current,
      }
      ro?.disconnect()
      sim.stop()
      simRef.current = null
      modelRef.current = null
      svg.on('.zoom', null).on('click', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the display props have their own effects
  }, [nodes, edges])

  // Layout switch: tree positions, or back to physics from the current positions.
  useEffect(() => {
    if (appliedLayoutRef.current === layout || !modelRef.current) return
    appliedLayoutRef.current = layout
    zoomRef.current?.scaleExtent(zoomExtent())
    // Back in Radial View from a Tree View zoomed out past Radial's floor: come up to the floor.
    if (svgRef.current && zoomRef.current && transformRef.current.k < zoomExtent()[0]) {
      select(svgRef.current).call(zoomRef.current.scaleTo, zoomExtent()[0])
    }
    if (layout === 'tree') {
      layoutTree()
      styleLinks()
    } else {
      // Back to physics from where the tree left the nodes. The root is the
      // pinned anchor of the radial layout, and the tree moved it; re-pin it
      // THERE, not back at the page origin, so the graph does not travel.
      const sim = simRef.current
      const rootNode = modelRef.current.byId.get(modelRef.current.rootIds[0])
      if (rootNode) { rootNode.fx = rootNode.x; rootNode.fy = rootNode.y }
      for (const d of modelRef.current.nodes) { d.vx = 0; d.vy = 0 }
      styleLinks()
      sim?.alpha(FORCE.entranceAlpha).restart()
      notifyOverview(true)
    }
    // No fit after a switch: the view stays where the reader left it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout])

  useEffect(() => {
    applyVisibility()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledTypes, filteredOutIds, showOrphans, keepUnlinked, showEntityLabels, showEdgeLabels, lineWidth])

  // External selection (the details panel's connection chips) focuses too.
  useEffect(() => {
    if (!modelRef.current || selectedRef.current === (selectedNodeId ?? null)) return
    applyFocus(selectedNodeId ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId])

  useEffect(() => {
    if (!modelRef.current) return
    applyFocus(selectedRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLink?.source, selectedLink?.target])

  useImperativeHandle(ref, () => ({
    setZoom: (level) => {
      if (!zoomRef.current || !svgRef.current) return
      const [kMin, kMax] = zoomExtent()
      select(svgRef.current).call(zoomRef.current.scaleTo, Math.max(kMin, Math.min(kMax, level)))
    },
    // Fit to screen snaps into place: the reader asked for no camera animation here.
    fit: () => fitTo(false),
    getZoom: () => transformRef.current.k,
    // 100% is natural size (k = 1): the spec's radii as drawn in the design's
    // reference frames, in both views. Not the fit-to-screen scale, which on
    // a hundred-document tenant shrank "100%" to unreadable.
    getFitZoom: () => 1,
    getMinZoom: () => zoomExtent()[0],
    getMaxZoom: () => zoomExtent()[1],
    subscribe: (fn) => { zoomSubs.current.add(fn); fn(transformRef.current.k); return () => void zoomSubs.current.delete(fn) },
    subscribeViewport: (fn) => { viewportSubs.current.add(fn); fn(viewport()); return () => void viewportSubs.current.delete(fn) },
    subscribeOverview: (fn) => {
      overviewSubs.current.add(fn)
      const ov = buildOverview()
      if (ov) fn(ov)
      return () => void overviewSubs.current.delete(fn)
    },
    focusNode: (id) => {
      if (!modelRef.current?.byId.has(id)) return
      applyFocus(id)
    },
    getOverview: buildOverview,
    replayEntrance: () => {
      if (!modelRef.current || !svgRef.current || !zoomRef.current) return
      selectedRef.current = null
      propsRef.current.onNodeSelect?.(null)
      applyFocus(null)
      select(svgRef.current).call(zoomRef.current.transform, zoomIdentity)
      runEntrance()
    },
    collapseToSites: () => {
      if (!modelRef.current) return
      collapseToDepth(modelRef.current, 1)
      render({ entering: true })
      if (propsRef.current.layout === 'tree') layoutTree()
      else simRef.current?.alpha(FORCE.expandAlpha).restart()
    },
    expandAll: () => {
      if (!modelRef.current) return
      expandAll(modelRef.current)
      render({ entering: true })
      if (propsRef.current.layout === 'tree') layoutTree()
      else simRef.current?.alpha(FORCE.expandAlpha).restart()
    },
    toggleChildren: (id) => {
      if (!modelRef.current) return false
      const ok = toggleChildren(modelRef.current, id)
      if (!ok) return false
      render({ entering: true })
      if (propsRef.current.layout === 'tree') layoutTree()
      else simRef.current?.alpha(FORCE.expandAlpha).restart()
      applyFocus(id)
      return true
    },
    isCollapsed: (id) => modelRef.current?.byId.get(id)?.collapsed ?? false,
    hasChildren: (id) => (modelRef.current?.byId.get(id)?.childIds.length ?? 0) > 0,
  }))

  return (
    <div ref={containerRef} className="h-full w-full bg-[#f5f5f4] font-sans dark:bg-neutral-900" data-testid="kg-d3-canvas">
      <svg ref={svgRef} className="block h-full w-full [&_g.node.dimmed]:opacity-15 [&_path.link.dimmed]:opacity-10 [&_g.node.selected_text.label]:fill-[#34728d] [&_g.node.selected_text.label]:font-medium" />
    </div>
  )
})

export default KnowledgeGraphD3Canvas
